import { Component, Input, Output, EventEmitter, OnInit, OnDestroy, HostListener, ElementRef, Renderer2, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { PermissionService } from '../../../../core/services/permission.service';
import { SYSTEM_PERMISSION_LIST } from '../../../constants/permissions.constants';

@Component({
  selector: 'app-profile-modal',
  standalone: true,
  imports: [CommonModule],
  template: `
    <div class="profile-modal-card" [style.top.px]="iconPosition.top + 8" [style.right.px]="iconPosition.right">
      <button class="profile-modal-close" (click)="close()">
        <img src="assets/icons/close.svg" alt="Close" class="close-icon" />
      </button>
      @if (profilePicture) {
        <div class="profile-modal-avatar">
          <img [src]="profilePicture" [alt]="name" class="profile-picture" />
        </div>
      } @else {
        <div class="profile-modal-avatar">{{ initials }}</div>
      }
      <div class="profile-modal-name">{{ name }}</div>
      <div class="profile-modal-email">{{ email }}</div>
      <div class="profile-modal-role">{{ role }}</div>
      <hr class="profile-modal-divider" />
      <button class="profile-modal-action" (click)="profileSettings.emit()">
        <img src="assets/icons/account_circle.svg" alt="Profile" class="profile-modal-action-icon" />
        Profile Settings
      </button>
      @if (!isAdminMode) {
        <button class="profile-modal-action"
                [class.disabled]="!hasAdminPermission()"
                [disabled]="!hasAdminPermission()"
                (click)="hasAdminPermission() && adminPortal.emit()">
          <img src="assets/icons/settings.svg" alt="Admin Portal" class="profile-modal-action-icon" />
          Admin Portal
        </button>
      }
      @if (isAdminMode) {
        <button class="profile-modal-action" type="button" (click)="exitAdminPortal.emit()">
          <img src="assets/icons/exit-admin.svg" alt="" class="profile-modal-action-icon profile-modal-action-icon--exit-admin" />
          Exit Admin Portal
        </button>
      }
      <button class="profile-modal-action" (click)="signOut.emit()">
        <img src="assets/icons/sign-out.svg" alt="Sign Out" class="profile-modal-action-icon" />
        Sign Out
      </button>
    </div>
  `,
  styleUrls: ['./profile-modal.component.scss']
})
export class ProfileModalComponent implements OnInit, OnDestroy {
  @Input() initials = '';
  @Input() name = '';
  @Input() email = '';
  @Input() role = '';
  @Input() profilePicture = '';
  @Input() iconPosition: { top: number; right: number } = { top: 0, right: 0 };
  @Input() isAdminMode = false;
  @Output() profileSettings = new EventEmitter<void>();
  @Output() adminPortal = new EventEmitter<void>();
  @Output() exitAdminPortal = new EventEmitter<void>();
  @Output() signOut = new EventEmitter<void>();
  @Output() closed = new EventEmitter<void>();

  private readonly permissionService = inject(PermissionService);

  constructor(private elementRef: ElementRef, private renderer: Renderer2) {}

  hasAdminPermission(): boolean {
    return this.permissionService.hasAnyPermissionSync([
      SYSTEM_PERMISSION_LIST.USERS_VIEW_ALL,
      SYSTEM_PERMISSION_LIST.ROLES_VIEW
    ]);
  }

  ngOnInit() {
    // Move modal to body to avoid stacking context issues
    document.body.appendChild(this.elementRef.nativeElement);

    // Add click listener to document to close modal when clicking outside
    document.addEventListener('click', this.handleDocumentClick);
  }

  ngOnDestroy() {
    // Remove click listener when component is destroyed
    document.removeEventListener('click', this.handleDocumentClick);

    // Remove modal from body when component is destroyed
    if (this.elementRef.nativeElement.parentNode) {
      this.elementRef.nativeElement.parentNode.removeChild(this.elementRef.nativeElement);
    }
  }

  @HostListener('click', ['$event'])
  onModalClick(event: Event) {
    // Prevent clicks inside the modal from bubbling up to document
    event.stopPropagation();
  }

  private handleDocumentClick = (event: Event) => {
    // Close modal when clicking outside
    this.close();
  };

  close() {
    this.closed.emit();
  }
}
